import { z } from "zod";

const validIdSchema = z.string().regex(/^[a-zA-Z0-9_-]+$/);

export const TYPE_SEPARATOR = "-";
export const GENERATION_VERSION_SEPARATOR = "@";
export const HISTORY_VERSION_SEPARATOR = ".";

export const idDocumentTypeStringMap = {
	draft: "Draft",
	whiteboard: "Whiteboard",
	card: "Card",
	preprint: "Preprint",
	threads: "Threads",
	chat: "Chat",
} as const;

export type DocumentUniqueId = z.infer<typeof validIdSchema>;

export type DocumentTypes =
	| "draft"
	| "whiteboard"
	| "card"
	| "preprint"
	| "threads"
	| "chat";

export type DocumentTypeString =
	(typeof idDocumentTypeStringMap)[DocumentTypes];

export type DocumentTypedIdString =
	| `${DocumentTypeString}${typeof TYPE_SEPARATOR}${DocumentUniqueId}`
	| `${DocumentTypeString}${typeof TYPE_SEPARATOR}${DocumentUniqueId}${typeof HISTORY_VERSION_SEPARATOR}${DocumentHistoryVersion}`
	| `${DocumentTypeString}${typeof TYPE_SEPARATOR}${DocumentUniqueId}${typeof GENERATION_VERSION_SEPARATOR}${DocumentGenerationVersion}`
	| `${DocumentTypeString}${typeof TYPE_SEPARATOR}${DocumentUniqueId}${typeof GENERATION_VERSION_SEPARATOR}${DocumentGenerationVersion}${typeof HISTORY_VERSION_SEPARATOR}${DocumentHistoryVersion}`;

export type DocumentGenerationVersion = number;
export type DocumentHistoryVersion = number;

// Zod schemas for validation
const documentTypeStringSchema = z.enum([
	"Whiteboard",
	"Card",
	"Preprint",
	"Draft",
	"Threads",
	"Chat",
]);

const documentGenerationVersionSchema = z.number().int().positive();

const documentHistoryVersionSchema = z.number().int().positive();

// Schema for the full DocumentTypedIdString format
export const documentTypedIdStringSchema = z.string().refine(
	(id) => {
		const dashIndex = id.indexOf(TYPE_SEPARATOR);
		if (dashIndex === -1) {
			return false;
		}

		const documentTypeString = id.substring(0, dashIndex);
		if (!documentTypeStringSchema.safeParse(documentTypeString).success) {
			return false;
		}

		const remaining = id.substring(dashIndex + 1);
		if (!remaining) {
			return false;
		}

		const generationVersionIndex = remaining.indexOf(
			GENERATION_VERSION_SEPARATOR,
		);
		const historyVersionIndex = remaining.indexOf(HISTORY_VERSION_SEPARATOR);
		const hasGenerationVersion = generationVersionIndex !== -1;
		const hasHistoryVersion = historyVersionIndex !== -1;

		if (hasGenerationVersion) {
			const generationVersionStr = remaining.substring(
				generationVersionIndex + 1,
				hasHistoryVersion ? historyVersionIndex : undefined,
			);
			const generationVersion = Number.parseInt(generationVersionStr, 10);

			if (
				!documentGenerationVersionSchema.safeParse(generationVersion).success
			) {
				return false;
			}
		}

		if (hasHistoryVersion) {
			const historyVersionStr = remaining.substring(historyVersionIndex + 1);
			const historyVersion = Number.parseInt(historyVersionStr, 10);

			if (!documentHistoryVersionSchema.safeParse(historyVersion).success) {
				return false;
			}
		}

		const uniqueIdEndIndex = Math.min(
			...[
				hasGenerationVersion ? generationVersionIndex : remaining.length,
				hasHistoryVersion ? historyVersionIndex : remaining.length,
			],
		);
		const documentUniqueId = remaining.substring(0, uniqueIdEndIndex);

		if (!validIdSchema.safeParse(documentUniqueId).success) {
			return false;
		}

		return true;
	},
	{
		message:
			"Invalid DocumentTypedIdString format. Expected format: DocumentTypeString-DocumentUniqueId or DocumentTypeString-DocumentUniqueId@DocumentGenerationVersion or DocumentTypeString-DocumentUniqueId.DocumentHistoryVersion or DocumentTypeString-DocumentUniqueId@DocumentGenerationVersion.DocumentHistoryVersion",
	},
);

export function isDocumentId(id: string): id is DocumentTypedIdString {
	return documentTypedIdStringSchema.safeParse(id).success;
}
